/* eslint-disable */
import "./SuperAdminOverview.css";
import React, { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { createAgreementInvite, deleteAgreementInvite } from "../services/agreementService";
import {
  agreementState,
  buildAgreementStats,
  buildDemoStats,
  customerHealth,
  formatDate,
  recentActivity,
} from "../services/dashboardService";

const LOGO_URL = "https://presentplus.uk/presentplus-logo-white.png";

export default function SuperAdminOverview() {
  const { profile, logout } = useAuth() || {};
  const [schools, setSchools] = useState([]);
  const [demoRequests, setDemoRequests] = useState([]);
  const [loadingSchools, setLoadingSchools] = useState(true);
  const [loadingDemos, setLoadingDemos] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [search, setSearch] = useState("");
  const [showInvite, setShowInvite] = useState(false);
  const [schoolName, setSchoolName] = useState("");
  const [adminFullName, setAdminFullName] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [plan, setPlan] = useState("pro");
  const [primaryColor, setPrimaryColor] = useState("#4F46E5");
  const [sending, setSending] = useState(false);
  const [busyId, setBusyId] = useState("");

  const stats = useMemo(() => buildAgreementStats(schools), [schools]);
  const demos = useMemo(() => buildDemoStats(demoRequests), [demoRequests]);
  const health = useMemo(() => customerHealth(schools), [schools]);
  const activity = useMemo(() => recentActivity(schools, demoRequests), [schools, demoRequests]);

  const filteredSchools = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return schools;
    return schools.filter((item) =>
      [item.schoolName, item.recipientName, item.recipientEmail, item.plan, item.status, agreementState(item).label]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [schools, search]);

  const signedInName = profile?.fullName || profile?.displayName || "Super Admin";
  const mfaComplete = Boolean(profile?.mfaCompleted || profile?.mfaEnabled || profile?.mfaSetupComplete);

  async function loadSchools() {
    try {
      setLoadingSchools(true);
      setError("");
      const callable = httpsCallable(functions, "listAgreementInvites");
      const response = await callable({});
      setSchools(Array.isArray(response.data?.invites) ? response.data.invites : []);
    } catch (err) {
      console.error(err);
      setError(err?.message || "Could not load customer accounts.");
    } finally {
      setLoadingSchools(false);
    }
  }

  useEffect(() => { loadSchools(); }, []);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, "demoRequests"),
      (snapshot) => {
        const rows = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        rows.sort((a, b) => {
          const getMs = (value) => {
            if (!value) return 0;
            if (typeof value.toMillis === "function") return value.toMillis();
            if (typeof value.toDate === "function") return value.toDate().getTime();
            if (typeof value.seconds === "number") return value.seconds * 1000;
            const parsed = new Date(value).getTime();
            return Number.isNaN(parsed) ? 0 : parsed;
          };
          return getMs(b.createdAt || b.submittedAt) - getMs(a.createdAt || a.submittedAt);
        });
        setDemoRequests(rows);
        setLoadingDemos(false);
      },
      (err) => {
        console.error(err);
        setLoadingDemos(false);
      }
    );
    return unsubscribe;
  }, []);

  async function handleInvite(event) {
    event.preventDefault();
    if (!schoolName.trim() || !adminFullName.trim() || !adminEmail.trim()) {
      setError("School name, representative name and email are required.");
      return;
    }
    try {
      setSending(true);
      setError("");
      setSuccess("");
      await createAgreementInvite({
        schoolName: schoolName.trim(),
        recipientName: adminFullName.trim(),
        recipientEmail: adminEmail.trim().toLowerCase(),
        plan,
        primaryColor: primaryColor.trim() || "#4F46E5",
      });
      setSuccess(`Agreement invitation sent to ${adminEmail.trim().toLowerCase()}.`);
      setSchoolName(""); setAdminFullName(""); setAdminEmail(""); setPlan("pro"); setPrimaryColor("#4F46E5");
      setShowInvite(false);
      await loadSchools();
    } catch (err) {
      console.error(err);
      setError(err?.message || "Agreement invitation failed.");
    } finally {
      setSending(false);
    }
  }

  async function handleResend(item) {
    const inviteId = item.inviteId || item.id;
    if (!inviteId) return setError("Invite ID is missing.");
    try {
      setBusyId(inviteId);
      setError("");
      const callable = httpsCallable(functions, "resendAgreementInvite");
      await callable({ inviteId });
      setSuccess(`Invitation resent to ${item.recipientEmail || "the school representative"}.`);
      await loadSchools();
    } catch (err) {
      setError(err?.message || "Could not resend invitation.");
    } finally { setBusyId(""); }
  }

  async function handleDelete(item) {
    const inviteId = item.inviteId || item.id;
    if (!inviteId) return setError("Invite ID is missing.");
    const confirmation = window.prompt(`Delete the invitation for ${item.schoolName || "this school"}? Type DELETE to confirm.`);
    if (confirmation !== "DELETE") return;
    try {
      setBusyId(inviteId);
      await deleteAgreementInvite(inviteId);
      setSchools((rows) => rows.filter((row) => (row.inviteId || row.id) !== inviteId));
      setSuccess("Agreement invitation deleted.");
    } catch (err) {
      setError(err?.message || "Could not delete invitation.");
    } finally { setBusyId(""); }
  }

  function openAgreement(item) {
    const state = agreementState(item);
    if (["signed", "active"].includes(state.key) && item.agreementId) {
      window.location.href = `/agreement-viewer?agreementId=${item.agreementId}`;
    } else if (["signed", "active"].includes(state.key)) {
      setError("No agreement ID was found for this record.");
    } else {
      handleResend(item);
    }
  }

  return (
    <div className="ce-overview">
      <section className="ce-hero">
        <div className="ce-hero-copy">
          <div className="ce-brandline"><img src={LOGO_URL} alt="PresentPlus" /> CoreLogic Enterprise</div>
          <p className="ce-eyebrow">Executive overview</p>
          <h1>One clear view of every customer journey.</h1>
          <p className="ce-hero-text">Track demand, agreements, activation and account health without entering a school’s operational data.</p>
          <div className="ce-hero-actions">
            <button className="ce-btn ce-btn-light" onClick={() => setShowInvite(true)}>+ New customer invite</button>
            <button className="ce-btn ce-btn-glass" onClick={() => window.location.href = "/super-admin/demo-requests"}>Open sales CRM</button>
          </div>
        </div>
        <div className="ce-user-card">
          <div className="ce-avatar">{signedInName.split(" ").map((x) => x[0]).slice(0,2).join("").toUpperCase()}</div>
          <div><span>Signed in as</span><strong>{signedInName}</strong><small>{mfaComplete ? "MFA protected" : "MFA setup required"}</small></div>
          <button onClick={async () => { if (logout) await logout(); }}>Sign out</button>
        </div>
      </section>

      {(error || success) && <div className={`ce-message ${error ? "is-error" : "is-success"}`}>{error || success}</div>}

      <section className="ce-kpi-grid">
        <Kpi title="Customer accounts" value={loadingSchools ? "…" : stats.total} note={`${stats.active} active`} icon="◫" />
        <Kpi title="New sales leads" value={loadingDemos ? "…" : demos.new} note={`${demos.total} total requests`} icon="↗" />
        <Kpi title="Signed agreements" value={loadingSchools ? "…" : stats.signed} note={`${stats.pending + stats.opened} still progressing`} icon="✓" />
        <Kpi title="Activation rate" value={loadingSchools ? "…" : `${health.activationRate}%`} note={`${health.healthy} live accounts`} icon="◎" />
      </section>

      <section className="ce-main-grid">
        <div className="ce-card ce-health-card">
          <CardHead eyebrow="Portfolio health" title="Customer account health" action="View accounts" onAction={() => window.location.href = "/super-admin/schools"} />
          <div className="ce-health-score">
            <div className="ce-ring" style={{ "--score": `${health.activationRate * 3.6}deg` }}>
              <div className="ce-ring-content">
                <strong>{health.activationRate}%</strong>
                <span>Health score</span>
              </div>
            </div>
          </div>
          <div className="ce-health-list">
            <HealthRow label="Healthy and active" value={health.healthy} tone="good" />
            <HealthRow label="In onboarding" value={health.onboarding} tone="progress" />
            <HealthRow label="Needs attention" value={health.attention} tone="warn" />
          </div>
        </div>

        <div className="ce-card ce-funnel-card">
          <CardHead eyebrow="Commercial pipeline" title="Sales to activation" action="Open CRM" onAction={() => window.location.href = "/super-admin/demo-requests"} />
          <FunnelRow label="Demo requests" value={demos.total} max={Math.max(demos.total, stats.total, 1)} />
          <FunnelRow label="Contacted / booked" value={demos.contacted + demos.booked} max={Math.max(demos.total, 1)} />
          <FunnelRow label="Agreements sent" value={stats.total} max={Math.max(demos.total, stats.total, 1)} />
          <FunnelRow label="Signed" value={stats.signed + stats.active} max={Math.max(stats.total, 1)} />
          <FunnelRow label="Activated" value={stats.active} max={Math.max(stats.total, 1)} />
        </div>

        <div className="ce-card ce-actions-card">
          <CardHead eyebrow="Command centre" title="Quick actions" />
          <button onClick={() => setShowInvite(true)}><span>＋</span><div><strong>Send agreement invite</strong><small>Start a secure customer onboarding journey</small></div></button>
          <button onClick={() => window.location.href = "/super-admin/demo-requests"}><span>↗</span><div><strong>Review demo requests</strong><small>{demos.new} lead{demos.new === 1 ? "" : "s"} awaiting review</small></div></button>
          <button onClick={() => window.location.href = "/super-admin/schools"}><span>◫</span><div><strong>Manage customer accounts</strong><small>Lifecycle, notes, audit and account status</small></div></button>
        </div>
      </section>

      <section className="ce-lower-grid">
        <div className="ce-card ce-activity-card">
          <CardHead eyebrow="Live intelligence" title="Recent activity" />
          <div className="ce-activity-list">
            {activity.length ? activity.map((item) => (
              <div className="ce-activity" key={item.id}>
                <span className={`ce-activity-icon is-${item.type}`}>{item.type === "demo" ? "↗" : "✓"}</span>
                <div><strong>{item.title}</strong><small>{item.description}</small></div>
                <time>{formatDate(item.date, true)}</time>
              </div>
            )) : <div className="ce-empty">No recent customer activity yet.</div>}
          </div>
        </div>

        <div className="ce-card ce-boundary-card">
          <p className="ce-eyebrow">Privacy boundary</p>
          <h2>Customer control, not school operations.</h2>
          <p>CoreLogic Enterprise manages commercial relationships, agreements, account lifecycle and support. It does not expose pupil, parent, staff, attendance, behaviour or school-reporting data.</p>
          <div className="ce-boundary-tags"><span>✓ Account lifecycle</span><span>✓ Agreement evidence</span><span>✓ Sales and support</span><span>✓ Audit visibility</span></div>
        </div>
      </section>

      <section className="ce-card ce-pipeline-card">
        <CardHead eyebrow="Agreement centre" title="Latest onboarding pipeline" action="Refresh" onAction={loadSchools} />
        <div className="ce-pipeline-toolbar"><input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search school, contact or status…" /><span>{filteredSchools.length} account{filteredSchools.length === 1 ? "" : "s"}</span></div>
        <div className="ce-table-wrap">
          <table className="ce-table"><thead><tr><th>Customer</th><th>Plan</th><th>Status</th><th>Last activity</th><th></th></tr></thead>
          <tbody>{loadingSchools ? <tr><td colSpan="5" className="ce-empty">Loading customer accounts…</td></tr> : filteredSchools.length ? filteredSchools.slice(0, 8).map((item) => {
            const state = agreementState(item); const id = item.inviteId || item.id;
            return <tr key={id || item.recipientEmail}><td><strong>{item.schoolName || "Unnamed school"}</strong><small>{item.recipientName || "School representative"} · {item.recipientEmail || "No email"}</small></td><td><span className="ce-plan">{item.plan || "Pro"}</span></td><td><span className={`ce-status is-${state.key}`}>{state.label}</span><small>{state.detail}</small></td><td>{formatDate(item.updatedAt || item.adminActivatedAt || item.signedAt || item.createdAt || item.invitedAt, true)}</td><td><div className="ce-row-actions"><button disabled={busyId === id} onClick={() => openAgreement(item)}>{busyId === id ? "Working…" : ["signed","active"].includes(state.key) ? "View" : "Resend"}</button><button className="is-delete" disabled={busyId === id} onClick={() => handleDelete(item)}>Delete</button></div></td></tr>;
          }) : <tr><td colSpan="5" className="ce-empty">No customer accounts match this search.</td></tr>}</tbody></table>
        </div>
      </section>

      {showInvite && <div className="ce-modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) setShowInvite(false); }}>
        <form className="ce-modal" onSubmit={handleInvite}>
          <div className="ce-modal-head"><div><p className="ce-eyebrow">New customer</p><h2>Send agreement invitation</h2></div><button type="button" onClick={() => setShowInvite(false)}>×</button></div>
          <label>School or organisation<input value={schoolName} onChange={(e) => setSchoolName(e.target.value)} placeholder="Example Primary School" /></label>
          <div className="ce-form-grid"><label>Representative name<input value={adminFullName} onChange={(e) => setAdminFullName(e.target.value)} placeholder="Full name" /></label><label>Representative email<input type="email" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} placeholder="admin@school.org.uk" /></label></div>
          <div className="ce-form-grid"><label>Plan<select value={plan} onChange={(e) => setPlan(e.target.value)}><option value="starter">Starter</option><option value="pro">Pro</option><option value="enterprise">Enterprise</option><option value="trial">Trial</option></select></label><label>Brand colour<div className="ce-colour-field"><input type="color" value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} /><input value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} /></div></label></div>
          <div className="ce-modal-actions"><button type="button" className="ce-btn ce-btn-cancel" onClick={() => setShowInvite(false)}>Cancel</button><button className="ce-btn ce-btn-primary" disabled={sending}>{sending ? "Sending…" : "Send invitation"}</button></div>
        </form>
      </div>}
    </div>
  );
}

function Kpi({ title, value, note, icon }) { return <div className="ce-kpi"><span className="ce-kpi-icon">{icon}</span><div><p>{title}</p><strong>{value}</strong><small>{note}</small></div></div>; }
function CardHead({ eyebrow, title, action, onAction }) { return <div className="ce-card-head"><div><p className="ce-eyebrow">{eyebrow}</p><h2>{title}</h2></div>{action && <button onClick={onAction}>{action} →</button>}</div>; }
function HealthRow({ label, value, tone }) { return <div className="ce-health-row"><span className={`ce-health-dot is-${tone}`} /><span>{label}</span><strong>{value}</strong></div>; }
function FunnelRow({ label, value, max }) { const width = Math.max(value ? 9 : 0, Math.min(100, Math.round((value / max) * 100))); return <div className="ce-funnel-row"><div><span>{label}</span><strong>{value}</strong></div><div className="ce-funnel-track"><i style={{ width: `${width}%` }} /></div></div>; }
